import trackingJS, { getTracking } from '@vp/tracking';

import { SampleDetails } from '../../../components/product-samples/types/sampleFlyoutData';
import { MerchandisingProductTrackingView } from '../../../components/specs-and-templates/types';
import { TemplateDownloadedFileType } from '../../../components/specs-and-templates/types';
import {
  EventInformation,
  PaypalClickedEventPayload,
  TrackingCategory,
} from '../../../domain/trackingConstants';

const FFlyoutTriggeredClickedEventName = 'Tab Selected';
const FlyoutCategory = 'Fly-Out';
const SamplesLabel = 'Quick Product View:Samples';

export const trackTemplateFileDownloaded = (
  fileType: TemplateDownloadedFileType,
  mpv: MerchandisingProductTrackingView
): void =>
  executeWithTracking((tracking) => {
    const eventName = 'File Downloaded';
    tracking.track(eventName, {
      category: 'Product Page',
      label: fileType,
      pageStage: 'Discover',
      pageSection: 'Product Page',
      pageName: `${mpv.mpvId}:Product Page`,
      product_id: mpv.mpvId,
      core_product_id: mpv.coreProductId,
    });
  });

export const sendFlyoutTriggerClickedEvent = (flyoutType: string, coreProductId: string): void =>
  executeWithTracking((tracking) => {
    tracking.track(FFlyoutTriggeredClickedEventName, {
      core_product_id: coreProductId,
      label: flyoutType,
    });
  });

export const trackDesignServicesCTAClicked = (
  mpvId: string,
  coreProductId: string,
  destinationUrl: string
): void =>
  executeWithTracking((tracking) => {
    tracking.track('Navigation Clicked', {
      pageSection: 'Product Page',
      pageStage: 'Discover',
      label: 'Design Services CTA link',
      product_id: mpvId,
      core_product_id: coreProductId,
      navigationDetail: 'See Design Services',
      eventDetail: `${getCurrentUrlPath()};${destinationUrl};Design Services Tiles;Design Services-CTA`,
    });
  });

export function sendPaypalClickedEvent(
  paypalMessageVariant: string,
  tenant: string,
  mpvId: string,
  coreProductId: string,
  eventInformation: EventInformation
): void {
  executeWithTracking((tracking) => {
    const payload: PaypalClickedEventPayload = {
      category: TrackingCategory.PRODUCT_PAGE,
      label: eventInformation.label,
      eventDetail: eventInformation.eventDetail,
      navigationDetail: eventInformation.navigationDetail,
      product_id: mpvId,
      core_product_id: coreProductId,
      trackingTenant: tenant,
      paypalMessageVariant,
    };
    tracking.track('PayPal Message Clicked', payload);
  });
}

export const trackSampleLinkOpened = (product: SampleDetails) => {
  executeWithTracking((tracking) => {
    const payload = {
      category: FlyoutCategory,
      label: SamplesLabel,
      eventDetail: product.productKey,
      core_product_id: product.baseProductKey,
      core_product_version: product.baseProductVersion,
      product_id: product.baseProductMpvId,
    };
    tracking.track('Fly-Out Viewed', payload);
  });
};

export const trackSampleLinkClosed = (product: SampleDetails) => {
  executeWithTracking((tracking) => {
    const payload = {
      category: FlyoutCategory,
      label: SamplesLabel,
      eventDetail: product.productKey,
      core_product_id: product.baseProductKey,
      core_product_version: product.baseProductVersion,
      product_id: product.baseProductMpvId,
    };
    tracking.track('Fly-Out Closed', payload);
  });
};

export const trackLearnMoreLinkClicked = (product: SampleDetails) => {
  executeWithTracking((tracking) => {
    const payload = {
      category: FlyoutCategory,
      label: SamplesLabel,
      eventDetail: `Learn More:${product.productKey}`,
      core_product_id: product.baseProductKey,
      core_product_version: product.baseProductVersion,
      product_id: product.baseProductMpvId,
    };
    tracking.track('Fly-Out Clicked', payload);
  });
};

export const trackCustomSampleLinkClicked = (product: SampleDetails) => {
  executeWithTracking((tracking) => {
    const payload = {
      category: FlyoutCategory,
      label: SamplesLabel,
      eventDetail: `Order single item:${product.productKey}`,
      core_product_id: product.baseProductKey,
      core_product_version: product.baseProductVersion,
      product_id: product.baseProductMpvId,
    };
    tracking.track('Fly-Out Clicked', payload);
  });
};

export const trackSampleAddToCartClicked = (product: SampleDetails) => {
  executeWithTracking((tracking) => {
    const payload = {
      category: FlyoutCategory,
      label: SamplesLabel,
      eventDetail: `Added from Quick View Flyout:${product.productKey}`,
      core_product_id: product.productKey,
      core_product_version: product.productVersion,
      product_id: product.mpvId,
    };
    tracking.track('Product Added', payload);
  });
};

const executeWithTracking = (fn: (tracking: trackingJS.Tracking) => void): void => {
  try {
    const tracking = getTracking();

    if (!tracking?.track) {
      window.addEventListener('trackingReady', () => {
        const tracking = getTracking();
        if (tracking?.track) {
          fn(tracking);
        }
      });
    } else {
      fn(tracking);
    }
  } catch (exception) {
    console.error('Failed to send tracking event', exception);
  }
};

function getCurrentUrlPath(): string | null {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.pathname;
  }
  return '';
}
